/**
 * Vessel and port, read out of a mail for an enquiry created without a person (2026-09-21).
 *
 * `Vessel` is named by IMO number (2,261 on the bench) with `vessel_name` beside it; `Port` by
 * name (87). A seven-digit IMO in the text is the strongest signal; otherwise a vessel's name as
 * a whole word, only when exactly one vessel bears it and it is long enough not to be a word of
 * English (`NORA` inside `honorable`). A port is its name in the text, longest first so
 * `FUJAIRAH ANCHORAGE` wins over a bare `FUJAIRAH`. Unmatched is `null`, never a default — the
 * header card asks for what the mail did not say.
 */

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const MIN_NAME = 4

export function pickVessel(text: string, vessels: readonly { code: string; name: string }[]): string | null {
  const imo = text.match(/\b(\d{7})\b/g) ?? []
  for (const n of imo) {
    const hit = vessels.find((v) => v.code === n)
    if (hit) return hit.code
  }
  const upper = text.toUpperCase()
  // Not followed by a numeral: `BLUE STAR II` in the mail is a sister ship of `BLUE STAR`, and
  // matching the shorter name would put the enquiry on the wrong vessel (bench, 2026-09-21).
  const hits = vessels.filter(
    (v) => v.name.trim().length >= MIN_NAME && new RegExp(`\\b${escape(v.name.trim().toUpperCase())}\\b(?!\\s*(?:[IVX]+|\\d+)\\b)`).test(upper),
  )
  if (hits.length === 1) return hits[0].code
  // Several: the longest name wins only if it is the sole longest.
  const longest = [...hits].sort((a, b) => b.name.length - a.name.length)
  return longest.length > 1 && longest[0].name.length > longest[1].name.length ? longest[0].code : null
}

export function pickPort(text: string, ports: readonly string[]): string | null {
  const upper = text.toUpperCase()
  const sorted = [...ports].filter((p) => p.trim().length >= MIN_NAME).sort((a, b) => b.length - a.length)
  for (const p of sorted) {
    if (new RegExp(`\\b${escape(p.trim().toUpperCase())}\\b`).test(upper)) return p
  }
  return null
}
